import React, { useState, useMemo, useCallback } from 'react';
import { IItemForGetShortInfoWithStatus, EntityStatus } from 'types';
import { SHOW_ARCHIVED_CUSTOMERS } from 'appConstants';
import { useTranslation } from 'react-i18next';
import { Rule } from 'antd/lib/form';
import { Checkbox } from 'antd';
import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import SearchField from 'components/SearchField/SearchField';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IProps {
	setIDsCustomer: (value: string[]) => void;
	refetch: () => void;
	customers: IItemForGetShortInfoWithStatus[];
	IDsSelectedCustomer: string[];
	responseError: boolean;
	isLoading: boolean;
}

export const MultipleSelect: React.FC<IProps> = React.memo(
	({ setIDsCustomer, refetch, customers, IDsSelectedCustomer, responseError, isLoading }) => {
		const [searchString, setSearchString] = useState<string>('');
		const [showArchived, setShowArchived] = useState<boolean>(
			localStorage.getItem(SHOW_ARCHIVED_CUSTOMERS) === 'true',
		);
		const { t } = useTranslation('error');
		const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectCustomer') }], [t]);

		const visibleCustomers = useMemo(() => {
			const items = showArchived
				? customers
				: customers.filter((el) => el.status === EntityStatus.Active || IDsSelectedCustomer.includes(el.id));

			return items.map((el) => ({
				...el,
				additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
			}));
		}, [customers, showArchived, IDsSelectedCustomer]);

		const filteredData = useMemo(() => {
			return searchString
				? visibleCustomers.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
				: visibleCustomers;
		}, [searchString, visibleCustomers]);

		const toggleShowArchived = useCallback(() => {
			localStorage.setItem(SHOW_ARCHIVED_CUSTOMERS, JSON.stringify(!showArchived));
			setShowArchived((prevValue) => !prevValue);
		}, [showArchived]);

		const handleSelectAllData = useCallback(() => {
			if (visibleCustomers.length === IDsSelectedCustomer.length) {
				setIDsCustomer([]);
			} else {
				const IDsOfAllCustomers = visibleCustomers.map((el) => el.id);
				setIDsCustomer(IDsOfAllCustomers);
			}
		}, [visibleCustomers, IDsSelectedCustomer, setIDsCustomer]);

		const renderSelectMenu = useCallback(
			(menu: React.ReactElement) => {
				return (
					<>
						<div className={style.selectMenuHeader}>
							<Checkbox
								className={style.checkBox}
								checked={
									!!(
										visibleCustomers.length &&
										visibleCustomers.length === IDsSelectedCustomer.length
									)
								}
								disabled={!visibleCustomers.length}
								onChange={handleSelectAllData}>
								<Text
									translationNameSpace="form"
									translationKey="selectAllCustomersLabel"
									asFragment={true}
								/>
							</Checkbox>
							<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
								<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
							</Checkbox>
							<SearchField
								onChangeSelectSearchString={setSearchString}
								searchString={searchString}
								className={style.searchField}
							/>
						</div>
						{menu}
					</>
				);
			},
			[
				visibleCustomers,
				IDsSelectedCustomer,
				handleSelectAllData,
				searchString,
				setSearchString,
				showArchived,
				toggleShowArchived,
			],
		);

		return (
			<FormSelect
				testId="CustomersMultipleSelect"
				name="selectedCustomers"
				label="customersLabel"
				mode="multiple"
				onRefetch={refetch}
				onChange={setIDsCustomer}
				dropdownRender={renderSelectMenu}
				loading={isLoading}
				disabled={isLoading}
				options={filteredData}
				fetchError={responseError}
				validationRules={validationRules}
				counterValue={IDsSelectedCustomer.length}
			/>
		);
	},
);
